/**
 * Shared types for the attachment subsystem (picker, chips, hook).
 *
 * These mirror the backend ChatAttachment shape (`backend/routers/chat_attachments.py`).
 * Keep them in sync — the picker validates against the server-side limits,
 * and the chip renders against the server-side status.
 */

export type AttachmentKind = 'tabular' | 'document' | 'image';

export type AttachmentStatus =
  | 'pending'      // queued, no work yet
  | 'processing'   // worker running
  | 'ready'        // worker done, usable
  | 'failed';      // worker failed; see error_message

export type ParseConfidence = 'high' | 'low' | null;

/**
 * One server-side attachment (post-upload, post-processing).
 *
 * Returned by `POST /attachments` and `GET /attachments/...`.
 */
export interface ServerAttachment {
  attachment_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  kind: AttachmentKind;
  status: AttachmentStatus;
  parse_confidence?: ParseConfidence;
  parse_confirmed_at?: string | null;
  error_message?: string | null;
  page_count?: number | null;
  char_count?: number | null;
  chunk_count?: number | null;
  preview_text?: string | null;
  schema_summary?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  /**
   * Set once the user removes the file from the composer.  The row is then
   * hidden from the composer chip strip and excluded from agent context,
   * but its metadata is still available for historical user-message chips
   * so the conversation history keeps showing the original filename.
   */
  deleted_at?: string | null;
}

/**
 * One client-side draft attachment (pre-upload, or while uploading).
 *
 * Lives only in the picker / hook state.  Once the server returns,
 * the entry transitions to a ServerAttachment (or `error`).
 */
export interface DraftAttachment {
  id: string;            // client-only UUID
  file: File;            // original File
  previewUrl?: string;   // for image variants
  uploadState:
    | 'queued'
    | 'uploading'
    | 'uploaded'         // server accepted; awaiting processing
    | 'error';
  uploadError?: string;
  serverAttachment?: ServerAttachment;  // populated post-upload
}

/**
 * Default file-type accept lists, by use case.
 */
export const ACCEPT_CHAT_ATTACHMENTS =
  '.csv,.tsv,.xlsx,.xls,.parquet,.pdf,.txt,.md,.docx,' +
  'text/csv,text/tab-separated-values,' +
  'application/vnd.ms-excel,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'application/pdf,text/plain,text/markdown,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const ACCEPT_IMAGES_ONLY = 'image/*';

/**
 * Friendly file-type hint shown beneath the picker button.
 */
export const CHAT_ATTACHMENTS_HINT =
  'CSV, Excel, Parquet, PDF, TXT, MD, DOCX · up to 25 MB each · 10 files per thread';

export const SUPPORT_IMAGE_HINT =
  'PNG, JPEG, GIF, WebP, etc. · up to 4 MB each · 15 MB total';
